import React, { useState, useEffect } from 'react';
import './ServicePriceManagement.scss';

interface ServicePrice {
  id: number;
  duration_days: number;
  price: number;
  currency: string;
}

interface Service {
  id: number;
  service_type: string;
  name: string;
  description: string;
  prices: ServicePrice[];
}

const ServicePriceManagement: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPrice, setEditingPrice] = useState<{ priceId: number; value: string } | null>(null);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        console.error('No admin token found');
        alert('Prisijunkite kaip administratorius');
        return;
      }

      const API_URL = '/api';
      const response = await fetch(`${API_URL}/services`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Services API Error:', response.status, errorText);
        alert(`Nepavyko užkrauti paslaugų: ${response.status}`);
        setServices([]);
        return;
      }

      const data = await response.json();
      console.log('Fetched services:', data);
      
      // Handle both direct array and wrapped response
      if (Array.isArray(data)) {
        setServices(data);
      } else if (data.data && Array.isArray(data.data)) {
        setServices(data.data);
      } else if (data.services && Array.isArray(data.services)) {
        setServices(data.services);
      } else {
        console.error('Unexpected response format:', data);
        setServices([]);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      alert('Klaida užkraunant paslaugas');
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPrice = (priceId: number, currentPrice: number) => {
    setEditingPrice({ priceId, value: currentPrice.toString() });
  };

  const handleSavePrice = async (priceId: number) => {
    if (!editingPrice) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/services/admin/prices/${priceId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ price: parseFloat(editingPrice.value) })
      });

      if (response.ok) {
        alert('Price updated successfully');
        setEditingPrice(null);
        fetchServices();
      } else {
        throw new Error('Failed to update price');
      }
    } catch (error) {
      console.error('Error updating price:', error);
      alert('Failed to update price');
    }
  };

  const handleCancelEdit = () => {
    setEditingPrice(null);
  };

  const formatDuration = (days: number): string => {
    if (days === 0) return 'Vienkartinė';
    if (days === 1) return '1 diena';
    if (days === 7) return '1 savaitė';
    if (days === 14) return '2 savaitės';
    if (days === 30) return '1 mėnuo';
    if (days === 90) return '3 mėnesiai';
    if (days === 365) return '1 metai';
    return `${days} dienų`;
  };

  if (loading) {
    return (
      <div className="service-price-management">
        <h2>Paslaugų kainų valdymas</h2>
        <p>Kraunama...</p>
      </div>
    );
  }

  return (
    <div className="service-price-management">
      <h2>Paslaugų kainų valdymas</h2>
      <p className="subtitle">Valdykite paslaugų kainas ir trukmę</p>

      <div className="services-list">
        {services.map((service) => (
          <div key={service.id} className="service-section">
            <h3>{service.name}</h3>
            <p className="service-description">{service.description}</p>

            <table className="price-table">
              <thead>
                <tr>
                  <th>Trukmė</th>
                  <th>Kaina</th>
                  <th>Veiksmai</th>
                </tr>
              </thead>
              <tbody>
                {service.prices.map((price) => (
                  <tr key={price.id}>
                    <td>{formatDuration(price.duration_days)}</td>
                    <td>
                      {editingPrice?.priceId === price.id ? (
                        <input
                          type="number"
                          step="0.01"
                          value={editingPrice.value}
                          onChange={(e) => setEditingPrice({ ...editingPrice, value: e.target.value })}
                          className="price-input"
                        />
                      ) : (
                        <span>{Number(price.price).toFixed(2)} {price.currency}</span>
                      )}
                    </td>
                    <td>
                      {editingPrice?.priceId === price.id ? (
                        <div className="action-buttons">
                          <button
                            className="btn-save"
                            onClick={() => handleSavePrice(price.id)}
                          >
                            Išsaugoti
                          </button>
                          <button
                            className="btn-cancel"
                            onClick={handleCancelEdit}
                          >
                            Atšaukti
                          </button>
                        </div>
                      ) : (
                        <button
                          className="btn-edit"
                          onClick={() => handleEditPrice(price.id, price.price)}
                        >
                          Redaguoti
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ServicePriceManagement;

